import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Sheet, SheetContent } from "@shopflo/ui/sheet";
import { useLocale } from "lib/hooks/useLocale";
import { AppliedCouponType } from "lib/contexts/CheckoutProvider";
import { currencyFormatter } from "lib/utils/formatters";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { CouponType } from "lib/types/checkout";
import { Star } from "@phosphor-icons/react";
import { AnimatedCounter } from "@shopflo/ui/ticker";
import { IMAGE_CDN_URL } from "lib/utils/constants";
import { motion } from "motion/react";
import { usePopupOrchestrator } from "lib/contexts/PopupOrchestratorProvider";

interface CelebrationPopupProps {
  isOpen: boolean;
  closePopup: () => void;
  appliedCouponCode: string;
  incomingCouponCode: string;
}

const CelebrationPopup: React.FC<CelebrationPopupProps> = ({
  isOpen,
  closePopup,
  appliedCouponCode,
  incomingCouponCode,
}) => {
  const { t } = useLocale();
  const {
    state: { billing, appliedCoupons, shippingHandles },
  } = useCheckoutContext();
  const {
    state: { checkoutMetadata, cartMetadata },
  } = useMerchantContext();

  const celebrationPopupConfig =
    checkoutMetadata?.uiAttributes?.celebrationPopupConfig ??
    cartMetadata?.uiAttributes?.celebrationPopupConfig;

  const [currentCoupon, setCurrentCoupon] = useState<AppliedCouponType>();
  const [couponType, setCouponType] = useState<"INCOMING" | "APPLIED" | "MULTIPLE">("INCOMING");
  const { autoTriggerConfig } = usePopupOrchestrator();
  const AUTO_CLOSE_DURATION = autoTriggerConfig.CELEBRATION.duration ?? 3000;

  const freebieAppliedCelebrationsText = Boolean(celebrationPopupConfig?.freebieAppliedCelebrationsText)
    ? celebrationPopupConfig?.freebieAppliedCelebrationsText
    : t("freebie_applied_celebrations_txt");

  const freeGiftsText = Boolean(celebrationPopupConfig?.freeGiftsText)
    ? celebrationPopupConfig?.freeGiftsText
    : t("free_gifts");

  useEffect(() => {
    if ((!appliedCouponCode || !incomingCouponCode) && !appliedCoupons?.length) return;

    const automaticPrepaidDiscounts = appliedCoupons.filter(
      (coupon) => coupon.autoApplied && coupon.isPrepaid,
    )?.length;

    if (appliedCouponCode) {
      const appliedCoupon = appliedCoupons.find(
        (coupon) => coupon.code.toLowerCase() === appliedCouponCode.toLowerCase(),
      );
      Boolean(appliedCoupon) && setCurrentCoupon(appliedCoupon);
      setCouponType("APPLIED");
      return;
    }
    if (incomingCouponCode) {
      if (appliedCoupons?.length - automaticPrepaidDiscounts > 1) {
        setCouponType("MULTIPLE");
        return;
      }
      const incomingCoupon = appliedCoupons.find(
        (coupon) => coupon.code.toLowerCase() === incomingCouponCode.toLowerCase(),
      );
      Boolean(incomingCoupon) && setCurrentCoupon(incomingCoupon);
      setCouponType("INCOMING");
      return;
    }
  }, [appliedCoupons, appliedCouponCode, incomingCouponCode]);

  useEffect(() => {
    if (!isOpen) return;

    const timeoutId = setTimeout(() => {
      closePopup();
    }, AUTO_CLOSE_DURATION);

    return () => clearTimeout(timeoutId);
  }, [isOpen, closePopup]);

  const getFreebiesCount = useCallback(() => {
    return appliedCoupons
      ?.filter((coupon: AppliedCouponType) => coupon?.isFreebie)
      ?.reduce((acc: number, coupon: AppliedCouponType) => coupon?.freebieItemCount + acc, 0);
  }, [appliedCoupons]);

  const rewardsAmount = useMemo(() => {
    return (
      appliedCoupons
        ?.filter((coupon: AppliedCouponType) => coupon.isReward)
        ?.reduce(
          (acc: number, coupon: AppliedCouponType) => (coupon?.couponRewardConfig?.amount ?? 0) + acc,
          0,
        ) ?? 0
    );
  }, [appliedCoupons]);

  const shippingDiscount = useMemo(() => {
    const hasAppliedShippingDiscount = appliedCoupons?.find(
      (coupon: AppliedCouponType) => coupon?.couponType === CouponType.SHIPPING,
    );
    if (!Boolean(hasAppliedShippingDiscount)) return 0;

    const selectedShippingHandle = shippingHandles?.find((handle) => handle?.selected_handle);
    const originalPrice = parseFloat(selectedShippingHandle?.original_price || "0");
    const discountPrice = parseFloat(selectedShippingHandle?.price || "0");
    const discount = originalPrice - discountPrice;

    return discount;
  }, [appliedCoupons, shippingHandles]);

  const getAppliedDiscountTitle = () => {
    const hasFreebies = Boolean(getFreebiesCount());
    const hasBillingDiscount = Boolean(billing?.discount);
    const isPrepaid = Boolean(currentCoupon?.isPrepaid);
    const couponTitle = currentCoupon?.title || currentCoupon?.code;

    if (couponType === "MULTIPLE") {
      return <>{hasFreebies ? freebieAppliedCelebrationsText : t("multiple_automatic_discounts_applied")}</>;
    }

    if (currentCoupon?.couponType === CouponType.SHIPPING) {
      return <>{t("shipping_discount_applied")}</>;
    }

    if (hasFreebies && hasBillingDiscount) {
      return <>{freebieAppliedCelebrationsText}</>;
    }

    if (hasBillingDiscount || isPrepaid) {
      return (
        <>
          {couponTitle}
          <span className="font-normal text-coal-light">&nbsp;{t("applied")}</span>
        </>
      );
    }

    return <>{freebieAppliedCelebrationsText}</>;
  };

  const getAppliedDiscountDesc = (): JSX.Element => {
    const hasDiscount = Boolean(billing?.discount);
    const isApplied = couponType === "APPLIED";
    const isFreebie = Boolean(currentCoupon?.isFreebie);
    let formattedPrice = currencyFormatter(currentCoupon?.discountValue);

    if (!isApplied || isFreebie || currentCoupon?.couponType === CouponType.PAYMENT_OFFER) {
      return <></>;
    }

    if (currentCoupon?.couponType === CouponType.SHIPPING) {
      const selectedShippingHandle = shippingHandles?.find((handle) => handle?.selected_handle);

      const originalPrice = parseFloat(selectedShippingHandle?.original_price || "0");
      const discountPrice = parseFloat(selectedShippingHandle?.price || "0");

      const discount = originalPrice - discountPrice;
      formattedPrice = currencyFormatter(discount);
    }

    if (!hasDiscount && currentCoupon?.couponType !== CouponType.SHIPPING) {
      return <></>;
    }

    return (
      <div className="flex items-center gap-1 rounded-lg px-2 py-1">
        <div className="flex items-center gap-1 bg-gradient-to-r from-yay-darker via-yay-darker/80 to-yay-dark bg-clip-text text-transparent text-sm font-semibold">
          <p>{t("offer_applied_desc")}</p>
          <p className="text-sm font-semibold">{formattedPrice}</p>
        </div>
      </div>
    );
  };

  const getTotalSavings = (): JSX.Element => {
    const isApplied = couponType === "APPLIED";
    const hasDiscount = Boolean(billing?.discount);
    const freebiesCount = getFreebiesCount();
    const hasFreebies = freebiesCount > 0;
    const isPrepaid = Boolean(currentCoupon?.isPrepaid);
    const isFreebie = Boolean(currentCoupon?.isFreebie);
    const isPaymentOffer = currentCoupon?.couponType === CouponType.PAYMENT_OFFER;
    const isShippingCoupon = currentCoupon?.couponType === CouponType.SHIPPING;

    const renderPrice = (value: number) => {
      const intergerValue = parseInt(String(value));

      return (
        <p className="text-xxl bg-gradient-to-r from-yay-darker via-yay-darker/5 to-yay-dark bg-clip-text text-transparent font-bold w-full flex">
          <span className="text-xxl bg-gradient-to-r from-yay-darker via-yay-darker/5 to-yay-dark bg-clip-text text-transparent font-bold w-full ">
            {"₹"}
          </span>
          <AnimatedCounter
            key={intergerValue}
            count={intergerValue}
            animationConfig={{
              startOffset: 4,
              initialDelay: 80,
              slowestDelay: 150,
              delayIncrement: 20,
              fastTicksCount: 4,
              finalDelay: 200,
            }}
          />
        </p>
      );
    };

    const renderFreebieText = () => {
      if (freebiesCount > 1) {
        return t("x_free_gifts", { count: freebiesCount });
      }
      return freeGiftsText;
    };

    if (isApplied) {
      if (isPrepaid) {
        return (
          <p className="text-base font-medium bg-gradient-to-r from-yay-darker via-yay-darker/10 to-yay-dark bg-clip-text text-transparent">
            {t("prepaid_discount_text", {
              discount: currencyFormatter(currentCoupon?.prepaidValue),
            })}
          </p>
        );
      }

      if (isFreebie) {
        return (
          <p className="text-base font-medium bg-gradient-to-r from-yay-darker via-yay-darker/10 to-yay-dark bg-clip-text text-transparent ">
            {renderFreebieText()}
          </p>
        );
      }

      if (isPaymentOffer) {
        <div className="flex flex-col items-center">
          <h3 className="text-base font-medium bg-gradient-to-r from-yay-darker via-yay-darker/10 to-yay-dark bg-clip-text text-transparent ">
            {t("bank_offer_applied")}
          </h3>
        </div>;
      }

      if (isShippingCoupon) {
        return (
          <div className="flex flex-col items-center gap-2">
            <h3 className="text-base font-medium bg-gradient-to-r from-yay-darker via-yay-darker/10 to-yay-dark bg-clip-text text-transparent ">
              {t("celebrations_total_discounts_strip")}
            </h3>
            <h4> {renderPrice(billing.discount + rewardsAmount + shippingDiscount)}</h4>
          </div>
        );
      }

      return (
        <div className="flex flex-col items-center gap-2">
          <h3 className="text-base font-medium bg-gradient-to-r from-yay-darker via-yay-darker/10 to-yay-dark bg-clip-text text-transparent ">
            {t("celebrations_total_discounts_strip")}
          </h3>
          <h4> {renderPrice(billing.discount + rewardsAmount + shippingDiscount)}</h4>
        </div>
      );
    }

    if (hasDiscount && hasFreebies) {
      return (
        <div className="flex flex-col items-center gap-2">
          <h3 className="text-base font-medium bg-gradient-to-r from-yay-darker via-yay-darker/10 to-yay-dark bg-clip-text text-transparent ">
            {t("free_gifts_and_savings")}
          </h3>
          <h4> {renderPrice(billing.discount + shippingDiscount)}</h4>
        </div>
      );
    }

    if (isPrepaid) {
      return (
        <p className="text-base font-medium bg-gradient-to-r from-yay-darker via-yay-darker/10 to-yay-dark bg-clip-text text-transparent">
          {t("prepaid_discount_text", {
            discount: currencyFormatter(currentCoupon?.prepaidValue),
          })}
        </p>
      );
    }

    if (hasFreebies) {
      return (
        <p className="text-base font-medium bg-gradient-to-r from-yay-darker via-yay-darker/10 to-yay-dark bg-clip-text text-transparent">
          {renderFreebieText()}
        </p>
      );
    }

    return (
      <div className="flex flex-col items-center gap-2">
        <h3 className="text-base font-medium bg-gradient-to-r from-yay-darker via-yay-darker/10 to-yay-dark bg-clip-text text-transparent ">
          {t("celebrations_total_discounts_strip")}
        </h3>
        <h4> {renderPrice(billing.discount + rewardsAmount + shippingDiscount)}</h4>
      </div>
    );
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      closePopup();
    }
  };

  if (!appliedCouponCode && !incomingCouponCode) return <></>;

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetContent
        side="bottom"
        withinContainer
        container={document.getElementById("checkout-ui-root")}
        className="!overflow-y-auto !border-0 !mt-auto !data-[state=open]:ui-bottom-0"
        onClick={() => closePopup()}
        overlayClassName="!bg-transparent">
        <div className="flex-1 flex flex-col w-full h-full ">
          <div className="flex flex-col w-full items-center h-full justify-center mt-12">
            <motion.div
              key={isOpen ? "open" : "closed"}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}>
              <img
                src={`${IMAGE_CDN_URL}/celebrationv2.svg`}
                alt="flo-logo-mono"
                className="h-32 bg-cover w-52"
              />
            </motion.div>
            <div className="flex flex-col gap-1">
              <div className="flex flex-col gap-1">
                <div className="p-2">
                  <div className="flex gap-1 justify-center">{getAppliedDiscountTitle()}</div>
                </div>
                <div className="flex text-center items-center w-full justify-center">
                  {getAppliedDiscountDesc()}
                </div>
              </div>
              <div className="p-2">
                <div className="relative flex items-center justify-center w-full">
                  <div className="flex-1 h-[1px] bg-gradient-to-r from-transparent via-gray-300 to-gray-300 opacity-60" />
                  <div className="px-2 text-gray-500">
                    <Star size={10} weight="fill" className="text-gray-500/20" />
                  </div>
                  <div className="flex-1 h-[1px] bg-gradient-to-l from-transparent via-gray-300 to-gray-300 opacity-60" />
                </div>
              </div>
              <div className="flex items-center w-full justify-center">
                <div className="flex text-center">{getTotalSavings()}</div>
              </div>
            </div>

            <div className=" w-full px-6 my-12">
              <button
                onClick={() => closePopup()}
                className="rounded-lg bg-yay-dark px-4 py-2 text-white font-semibold transition-colors w-full focus:border-none outline-none relative overflow-hidden">
                <span className="relative z-10 lower-case">
                  {celebrationPopupConfig?.celebrationsTotalSavingsBtnText ||
                    t("celebrations_total_savings_btn_text")}
                </span>
                <div
                  className="absolute top-0 right-0 h-full w-full bg-white opacity-20 animate-shrink-width"
                  style={{ animationDuration: `${AUTO_CLOSE_DURATION}ms` }}
                />
              </button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CelebrationPopup;
